import { createAction, createAsyncThunk, createReducer } from '@reduxjs/toolkit';
import { API, SCOPE_CRM_COMPANIES } from '../../constants';
import { application } from '../../services/application';
import { ANY_OF_THOSE } from '../../constants/filter';
import { getFilterParams } from '../../utils/filterUtils';
import { INITIAL_LIST_STATE_EXTENDED } from '../helpers/common';
import { extendListBuilder, generateListActions } from '../helpers/listActionsHelpers';
import { extendBuilderWithFacetCounts, extendBuilderWithListControls } from '../helpers/sharedCases';

const FIELD_FILTER_KEYS = ['status', 'companyType', 'createdAt', 'tags', 'source', 'companySize', 'setAsides'];

export const INITIAL_STATE = {
  ...INITIAL_LIST_STATE_EXTENDED,
  order: ['name', 'asc'],
  pagination: { page: 1, perPage: 20 },
  filters: {
    text: '',
    fields: {
      status: { data: ['active'], type: ANY_OF_THOSE },
    },
  },
  selectedCompany: null,
  companyPending: false,
  savePending: false,
  createPending: false,
  importPending: false,
  facetCounts: null,
  facetCountsPending: false,
  facetCountsRequestId: null,
};

const listActions = generateListActions({
  scope: SCOPE_CRM_COMPANIES,
  apiMethod: {
    GET_LIST: API.CRM_COMPANIES.GET_LIST,
  },
  getStore: (store) => store.crmCompanies,
});

export const clearSelectedCompany = createAction(`${SCOPE_CRM_COMPANIES}/clearSelectedCompany`);

export const fetchCompany = createAsyncThunk(`${SCOPE_CRM_COMPANIES}/fetchCompany`, (params) => {
  return application.call(API.CRM_COMPANIES.FIND_ONE, params);
});

export const updateCompany = createAsyncThunk(`${SCOPE_CRM_COMPANIES}/updateCompany`, (params) => {
  return application.call(API.CRM_COMPANIES.UPDATE, params);
});

export const createCompany = createAsyncThunk(`${SCOPE_CRM_COMPANIES}/createCompany`, (params) => {
  return application.call(API.CRM_COMPANIES.CREATE, params);
});

export const importOrganizationsCsv = createAsyncThunk(`${SCOPE_CRM_COMPANIES}/importOrganizationsCsv`, (params) => {
  return application.call(API.CRM_COMPANIES.IMPORT_CSV, params);
});

export const bulkUpdateCompanies = createAsyncThunk(`${SCOPE_CRM_COMPANIES}/bulkUpdateCompanies`, (params) => {
  return application.call(API.CRM_COMPANIES.BULK_UPDATE, params);
});

export const fetchFacetCounts = createAsyncThunk(
  `${SCOPE_CRM_COMPANIES}/fetchFacetCounts`,
  (activityWindow, { getState }) => {
    const { text, fields } = getState().crmCompanies.filters;

    return application.call(API.CRM_COMPANIES.GET_FACET_COUNTS, {
      text: text || '',
      filters: getFilterParams(fields),
      activityWindow: activityWindow || undefined,
    });
  }
);

export const setCompanyListFilters = createAction(`${SCOPE_CRM_COMPANIES}/setCompanyListFilters`);
export const setCompanyListOrder = createAction(`${SCOPE_CRM_COMPANIES}/setCompanyListOrder`);
export const setCompanyListPage = createAction(`${SCOPE_CRM_COMPANIES}/setCompanyListPage`);
export const setCompanyListPerPage = createAction(`${SCOPE_CRM_COMPANIES}/setCompanyListPerPage`);

export const actions = {
  ...listActions,
  clearSelectedCompany,
  fetchCompany,
  updateCompany,
  createCompany,
  importOrganizationsCsv,
  bulkUpdateCompanies,
  fetchFacetCounts,
  setCompanyListFilters,
  setCompanyListOrder,
  setCompanyListPage,
  setCompanyListPerPage,
};

export default createReducer(INITIAL_STATE, (builder) => {
  extendListBuilder(builder, actions);

  extendBuilderWithListControls(
    builder,
    {
      setFilters: setCompanyListFilters,
      setOrder: setCompanyListOrder,
      setPage: setCompanyListPage,
      setPerPage: setCompanyListPerPage,
    },
    FIELD_FILTER_KEYS
  );

  builder
    .addCase(fetchCompany.pending, (state) => {
      state.companyPending = true;
    })
    .addCase(fetchCompany.fulfilled, (state, action) => {
      state.selectedCompany = action.payload;
      state.companyPending = false;
    })
    .addCase(fetchCompany.rejected, (state) => {
      state.selectedCompany = null;
      state.companyPending = false;
    })
    .addCase(updateCompany.pending, (state) => {
      state.savePending = true;
    })
    .addCase(updateCompany.fulfilled, (state, action) => {
      state.savePending = false;
      const company = action.payload?.company;

      if (company) {
        state.selectedCompany = company;
        const index = state.items.findIndex((item) => item.id === company.id);
        if (index >= 0) {
          state.items[index] = company;
        }
      }
    })
    .addCase(updateCompany.rejected, (state) => {
      state.savePending = false;
    })
    .addCase(createCompany.pending, (state) => {
      state.createPending = true;
    })
    .addCase(createCompany.fulfilled, (state) => {
      state.createPending = false;
    })
    .addCase(createCompany.rejected, (state) => {
      state.createPending = false;
    })
    .addCase(importOrganizationsCsv.pending, (state) => {
      state.importPending = true;
    })
    .addCase(importOrganizationsCsv.fulfilled, (state) => {
      state.importPending = false;
    })
    .addCase(importOrganizationsCsv.rejected, (state) => {
      state.importPending = false;
    })
    .addCase(clearSelectedCompany, (state) => {
      state.selectedCompany = null;
      state.companyPending = false;
      state.savePending = false;
    });
  extendBuilderWithFacetCounts(builder, fetchFacetCounts);

  return builder;
});
